"use strict";

/**
 * Cart Modal (safe refresh)
 * Fix for stores where "/?section_id=s-cart-modal" returns 500.
 *
 * Strategy:
 * 1) Prefer "/cart?section_id=<handle>" (usually stable)
 * 2) Fallback to "/?section_id=<handle>"
 * 3) Fallback to "/?sections=<handle>" (JSON endpoint)
 */
(function () {
  class SCartModal extends HTMLElement {
    constructor() {
      super();

      this.sectionHandle = null;

      this.body = null;
      this.overlay = null;
      this.headerItemCount = null;
      this.btnCloser = null;

      /* A11Y-02 (#109): the line to announce once the drawer has re-rendered. The header button
         (#HeaderCartTrigger) is not kept here: a header section reload replaces it, so openModal
         and closeModal look it up each time (global constraint). */
      this.pendingAdded = null;
      /* A11Y-02 (#109): unsubscribe hooks for the two PubSub topics, released on disconnectedCallback
         so a Theme Editor reload's detached instance cannot still answer the next cart-modal:open */
      this.unsubscribeOpen = null;
      this.unsubscribeUpdate = null;

      this.recommendationAccordion = null;
      this.recommendationToggle = null;
      this.recommendationPanel = null;
      this.onRecommendationToggle = null;

      this.updateCart = this.updateCart.bind(this);
      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
    }

    connectedCallback() {
      this.sectionHandle = this.dataset.sectionId || "s-cart-modal";

      this.body = document.body;
      this.overlay = document.querySelector(".js-overlay");
      this.headerItemCount = document.querySelector(".cart-link__bubble-num");

      this.bindCloser();
      this.bindRecommendationAccordion();

      if (window.PubSub?.subscribe) {
        this.unsubscribeOpen = window.PubSub.subscribe("cart-modal:open", this.openModal);
        this.unsubscribeUpdate = window.PubSub.subscribe("cart:update", this.updateCart);
      }

      document.addEventListener("DOMContentLoaded", () => {
        this.style.display = "";
      });
    }

    disconnectedCallback() {
      this.unbindCloser();
      this.unbindOverlay();
      this.unbindRecommendationAccordion();
      /* A11Y-02 (#109): release the stale PubSub subscriptions too - otherwise a detached instance
         still answers the next cart-modal:open and drives CDialog against a root that has left the
         page, inerting the live drawer's siblings and never releasing them */
      this.unsubscribeOpen?.();
      this.unsubscribeUpdate?.();
      this.unsubscribeOpen = null;
      this.unsubscribeUpdate = null;
      /* A11Y-02 (#109): a Theme Editor reload replaces this element; release the page it made inert */
      window.CDialog?.close(this, { restoreFocus: false });
    }

    bindCloser() {
      this.btnCloser = this.querySelector(".js-cart-modal__closer");
      if (!this.btnCloser) return;

      this.btnCloser.removeEventListener("click", this.closeModal);
      this.btnCloser.addEventListener("click", this.closeModal);
    }

    unbindCloser() {
      if (!this.btnCloser) return;
      this.btnCloser.removeEventListener("click", this.closeModal);
    }

    bindOverlay() {
      if (!this.overlay) return;

      this.overlay.removeEventListener("click", this._onOverlayClick);
      this.overlay.addEventListener("click", this._onOverlayClick);
    }

    unbindOverlay() {
      if (!this.overlay) return;
      this.overlay.removeEventListener("click", this._onOverlayClick);
    }

    bindRecommendationAccordion() {
      this.recommendationAccordion = this.querySelector("[data-cart-recommendation-accordion]");
      this.recommendationToggle = this.querySelector("[data-cart-recommendation-toggle]");
      this.recommendationPanel = this.querySelector("[data-cart-recommendation-panel]");

      if (!this.recommendationAccordion || !this.recommendationToggle || !this.recommendationPanel) return;

      this.onRecommendationToggle = this.onRecommendationToggle || this.toggleRecommendation.bind(this);

      this.recommendationToggle.removeEventListener("click", this.onRecommendationToggle);
      this.recommendationToggle.addEventListener("click", this.onRecommendationToggle);
    }

    unbindRecommendationAccordion() {
      if (!this.recommendationToggle || !this.onRecommendationToggle) return;
      this.recommendationToggle.removeEventListener("click", this.onRecommendationToggle);
    }

    toggleRecommendation() {
      if (!this.recommendationAccordion || !this.recommendationToggle || !this.recommendationPanel) return;

      const isOpen = this.recommendationAccordion.classList.contains("is-open");

      this.recommendationAccordion.classList.toggle("is-open", !isOpen);
      this.recommendationToggle.setAttribute("aria-expanded", String(!isOpen));
      this.recommendationPanel.hidden = isOpen;
    }

    _onOverlayClick() {
      this.closeModal();
    }

    openModal(item) {
      /* A11Y-02 (#109): the drawer is a modal dialog. CDialog (assets/c-dialog.js) makes the page
         behind it inert (the overlay stays clickable), moves focus onto the drawer itself - its
         contents are re-rendered a moment later, so a control inside would lose focus - closes it
         on Escape, and hands focus back on close. A second open while it is open (the cross-sell
         adds from inside the drawer) leaves focus where it is. The added line, when a caller sends
         one with cart-modal:open, is announced once the re-render has the new count. */
      /* A11Y-02 (#109): belt-and-braces against disconnectedCallback's unsubscribe - a detached
         instance must never drive CDialog against a root that has left the page */
      if (!this.isConnected) return;
      const wasOpen = this.classList.contains("is-open");
      this.pendingAdded = item && (item.title || item.product_title) ? item : null;

      this.body?.classList.add("modal-open");
      this.overlay?.classList.add("is-open");
      this.classList.add("is-open");

      if (!wasOpen) {
        /* A11Y-01 (#108), A11Y-02 (#109): aria-expanded on the header button follows the drawer;
           the button is looked up now, not cached, since a header reload replaces it */
        const trigger = document.querySelector("#HeaderCartTrigger");
        trigger?.setAttribute("aria-expanded", "true");
        window.CDialog?.open({
          root: this,
          keep: this.overlay ? [this.overlay] : [],
          initialFocus: this,
          fallbackFocus: trigger,
          onEscape: this.closeModal
        });
      }

      this.bindOverlay();
      this.updateCart();
    }

    closeModal() {
      this.body?.classList.remove("modal-open");
      this.overlay?.classList.remove("is-open");
      this.classList.remove("is-open");

      this.unbindOverlay();

      /* A11Y-02 (#109): the header button reports the drawer closed; CDialog releases the page and
         returns focus to whatever opened the drawer (the header button when that is gone). The
         button is looked up now, so a header reload while the drawer was open updates the live one. */
      document.querySelector("#HeaderCartTrigger")?.setAttribute("aria-expanded", "false");
      window.CDialog?.close(this);
    }

    /* A11Y-02 (#109): swap in the fresh drawer markup without losing the two things a keyboard or
       screen-reader user is holding on to. The [data-cart-status] live region stays the same node
       (a region that is removed and re-inserted is not reliably announced), and focus goes back to
       the same control of the same cart line - or to the drawer itself when that line is gone -
       instead of falling to <body>. */
    replaceContent(freshCart) {
      const status = this.querySelector("[data-cart-status]");
      const active = document.activeElement;
      const hadFocus = Boolean(active && active !== this && this.contains(active));
      const line = hadFocus && active.closest ? active.closest("c-cart-item") : null;
      const lineId = line ? line.getAttribute("data-id") : null;
      const hook = hadFocus
        ? Array.from(active.classList || []).find((name) => name.indexOf("js-") === 0) || null
        : null;

      Array.from(this.children).forEach((child) => {
        if (child !== status) child.remove();
      });
      this.insertAdjacentHTML("beforeend", freshCart.innerHTML);
      if (status) {
        this.querySelectorAll("[data-cart-status]").forEach((node) => {
          if (node !== status) node.remove();
        });
      }

      if (!hadFocus) return;
      /* A11Y-02 (#109): only chase the hook inside the surviving cart line the focused control was
         in. A control with no line (the in-drawer cross-sell) has no safe re-render target to hunt
         for across the whole drawer - the cross-sell panel re-renders `hidden`, so a match there
         would silently refuse focus() and leave it on <body> - so it goes straight to the root. */
      const scope = lineId ? this.querySelector(`c-cart-item[data-id="${lineId}"]`) : null;
      const target = (hook && scope && scope.querySelector(`.${hook}`)) || this;
      target.focus({ preventScroll: true });
      /* A11Y-02 (#109): the resolved target can still refuse focus (hidden, disabled, removed by the
         time this runs) - never leave it on <body>, land it on the drawer itself */
      if (target !== this && document.activeElement !== target) {
        this.focus({ preventScroll: true });
      }
    }

    /* A11Y-02 (#109): the only writer of the drawer's live region: "Added to your cart: <title>.
       <count>", with the label from info.cart_item_added and the count the heading already prints
       ("3 items"). Cleared first and written a moment later, so adding the same thing twice is
       still announced. */
    announceAdded(item) {
      const status = this.querySelector("[data-cart-status]");
      const title = item ? item.title || item.product_title || "" : "";
      if (!status || !title) return;

      const countEl = this.querySelector(".js-cart-modal__count");
      const count = countEl ? countEl.textContent.replace(/\s+/g, " ").trim() : "";
      const label = status.getAttribute("data-added-label") || "Added to your cart";
      const message = count ? `${label}: ${title}. ${count}` : `${label}: ${title}.`;

      status.textContent = "";
      setTimeout(() => {
        status.textContent = message;
      }, 100);
    }

    async fetchSectionHTML() {
      const root = window.Shopify?.routes?.root || "/";
      const handle = encodeURIComponent(this.sectionHandle);

      const candidates = [
        `${root}cart?section_id=${handle}`,
        `${root}?section_id=${handle}`
      ];

      for (const url of candidates) {
        try {
          const response = await fetch(url, {
            credentials: "same-origin",
            cache: "no-store"
          });

          if (!response.ok) continue;

          const html = await response.text();

          if (html && html.includes("s-cart-modal")) {
            return html;
          }
        } catch (error) {
          console.warn("[s-cart-modal] section fetch failed:", url, error);
        }
      }

      try {
        const response = await fetch(`${root}?sections=${handle}`, {
          credentials: "same-origin",
          cache: "no-store"
        });

        if (response.ok) {
          const data = await response.json();
          const html = data?.[this.sectionHandle];

          if (html && html.includes("s-cart-modal")) {
            return html;
          }
        }
      } catch (error) {
        console.warn("[s-cart-modal] JSON section fetch failed:", error);
      }

      return null;
    }

    async updateCart() {
      /* A11Y-02 (#109): same belt-and-braces as openModal - a detached instance must not fetch and
         re-render either */
      if (!this.isConnected) return;
      this.classList.add("is-loading");

      const currentBody = this.querySelector(".js-cart-modal__body");
      const previousScrollTop = currentBody ? currentBody.scrollTop : 0;

      try {
        const html = await this.fetchSectionHTML();

        if (!html) {
          console.error("[s-cart-modal] Could not fetch section HTML.");
          return;
        }

        const temp = document.createElement("div");
        temp.innerHTML = html;

        const freshCart = temp.querySelector(".s-cart-modal");

        if (!freshCart) {
          console.error("[s-cart-modal] Section HTML did not include .s-cart-modal.");
          return;
        }

        /* A11Y-02 (#109): no wholesale innerHTML swap any more - it threw away the live region and
           the control the shopper had focus on. See replaceContent. */
        this.replaceContent(freshCart);

        const countEl = this.querySelector(".js-cart-modal__count");

        if (countEl && this.headerItemCount) {
          this.headerItemCount.innerHTML = countEl.dataset.cartCount || "";
        }

        this.bindCloser();
        this.bindRecommendationAccordion();

        const newBody = this.querySelector(".js-cart-modal__body");

        if (newBody) {
          newBody.scrollTop = previousScrollTop;
        }

        /* A11Y-02 (#109): announce the added line only now, when the count in the heading is the new one */
        if (this.pendingAdded) {
          this.announceAdded(this.pendingAdded);
          this.pendingAdded = null;
        }

        document.dispatchEvent(
          new CustomEvent("cart-modal:updated", {
            detail: {
              cartModal: this
            }
          })
        );
      } catch (error) {
        console.error("[s-cart-modal] updateCart failed:", error);
      } finally {
        this.classList.remove("is-loading");
      }
    }
  }

  if (!customElements.get("s-cart-modal")) {
    customElements.define("s-cart-modal", SCartModal);
  }
})();
